/**
 * Notification Component JavaScript
 * Programmatic notification/toast system with GSAP animations
 */

(function() {
  'use strict';

  const defaultOptions = {
    type: 'default', // 'default' | 'info' | 'success' | 'warning' | 'error' | 'cart'
    title: '',
    message: '',
    duration: 4500, // Auto-close after ms (0 = no auto-close)
    closable: true,
    position: 'top-right', // 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
    showProgress: false, // Show progress bar for auto-dismiss
    image: null, // Optional image URL (for cart notifications)
    imageAlt: '' // Image alt text
  };

  let notificationId = 0;
  const containers = {};
  const activeNotifications = new Map(); // Track active notifications and their timers

  /**
   * Get or create a notification container for a position
   */
  function getContainer(position) {
    if (containers[position]) {
      return containers[position];
    }

    const container = document.createElement('div');
    container.className = `notification-container notification-container--${position}`;
    container.setAttribute('role', 'region');
    container.setAttribute('aria-label', 'Notifications');
    document.body.appendChild(container);
    containers[position] = container;
    return container;
  }

  /**
   * Get icon name based on type
   */
  function getIcon(type) {
    const icons = {
      info: 'info',
      success: 'check-circle',
      warning: 'alert-triangle',
      error: 'x-circle',
      cart: 'shopping-bag'
    };
    return icons[type] || null;
  }

  /**
   * Get title icon based on type
   */
  function getTitleIcon(type) {
    const icons = {
      info: 'info',
      success: 'circle-check',
      warning: 'triangle-alert',
      error: 'circle-x',
      cart: 'circle-check'
    };
    return icons[type] || null;
  }

  /**
   * Check if GSAP is available
   */
  function hasGSAP() {
    return typeof gsap !== 'undefined';
  }

  /**
   * Create a notification element
   */
  function createNotification(options) {
    const id = `notification-${++notificationId}`;
    const icon = getIcon(options.type);
    const hasIcon = icon !== null && !options.image;
    const hasImage = options.image !== null;

    const notification = document.createElement('div');
    notification.className = `notification notification--${options.type}`;
    if (hasImage) {
      notification.classList.add('notification--has-image');
    }
    notification.setAttribute('data-notification', id);
    notification.setAttribute('role', 'alert');

    let html = '';

    // Image (for cart notifications)
    if (hasImage) {
      html += `
        <div class="notification__image">
          <img src="${options.image}" alt="${options.imageAlt || ''}" loading="lazy">
        </div>
      `;
    }
    // Icon (if no image)
    else if (hasIcon) {
      html += `
        <span class="notification__icon">
          <i data-lucide="${icon}"></i>
        </span>
      `;
    }

    // Content
    html += '<div class="notification__content">';
    if (options.title) {
      const titleIcon = getTitleIcon(options.type);
      if (titleIcon) {
        html += `<div class="notification__title">${options.title}<i data-lucide="${titleIcon}" class="notification__title-icon"></i></div>`;
      } else {
        html += `<div class="notification__title">${options.title}</div>`;
      }
    }
    if (options.message) {
      html += `<div class="notification__message">${options.message}</div>`;
    }
    html += '</div>';

    // Close button
    if (options.closable) {
      html += `
        <button type="button" class="notification__close" data-notification-close aria-label="Close notification">
          <i data-lucide="x"></i>
        </button>
      `;
    }

    // Progress bar for auto-dismiss
    if (options.showProgress && options.duration > 0) {
      html += `<div class="notification__progress"><div class="notification__progress-bar"></div></div>`;
    }

    notification.innerHTML = html;

    return { element: notification, id };
  }

  /**
   * Animate notification entrance with GSAP
   */
  function animateIn(element, position) {
    if (!hasGSAP()) {
      // Fallback: just make it visible
      element.style.opacity = '1';
      element.style.transform = 'translateX(0)';
      return Promise.resolve();
    }

    // Set initial state
    const isRight = position.includes('right');
    const xOffset = isRight ? 100 : -100;

    gsap.set(element, {
      opacity: 0,
      x: xOffset,
      scale: 0.95
    });

    // Animate with spring-like physics
    return gsap.to(element, {
      opacity: 1,
      x: 0,
      scale: 1,
      duration: 0.5,
      ease: 'back.out(1.4)',
      clearProps: 'transform'
    });
  }

  /**
   * Animate notification exit with GSAP
   */
  function animateOut(element, position) {
    return new Promise((resolve) => {
      if (!hasGSAP()) {
        // Fallback: use CSS animation
        element.setAttribute('data-closing', '');
        element.addEventListener('animationend', () => {
          resolve();
        }, { once: true });
        return;
      }

      const isRight = position.includes('right');
      const xOffset = isRight ? 100 : -100;

      gsap.to(element, {
        opacity: 0,
        x: xOffset,
        scale: 0.95,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: resolve
      });
    });
  }

  /**
   * Animate progress bar
   */
  function animateProgress(element, duration) {
    const progressBar = element.querySelector('.notification__progress-bar');
    if (!progressBar) return null;

    if (hasGSAP()) {
      return gsap.fromTo(progressBar, 
        { scaleX: 1 },
        { 
          scaleX: 0, 
          duration: duration / 1000,
          ease: 'linear',
          transformOrigin: 'left center'
        }
      );
    } else {
      // CSS fallback
      progressBar.style.transition = `transform ${duration}ms linear`;
      progressBar.style.transform = 'scaleX(0)';
      return null;
    }
  }

  /**
   * Close a notification
   */
  async function close(id) {
    const notification = document.querySelector(`[data-notification="${id}"]`);
    if (!notification) return;

    // Get notification data
    const notifData = activeNotifications.get(id);
    if (notifData) {
      // Clear auto-close timer
      if (notifData.timer) {
        clearTimeout(notifData.timer);
      }
      // Kill progress animation
      if (notifData.progressTween) {
        notifData.progressTween.kill();
      }
    }

    // Animate out
    const position = notifData?.position || 'top-right';
    await animateOut(notification, position);

    // Remove from DOM
      notification.remove();

    // Clean up tracking
    activeNotifications.delete(id);
  }

  /**
   * Show a notification
   */
  function show(options) {
    const opts = { ...defaultOptions, ...options };
    const container = getContainer(opts.position);
    const { element, id } = createNotification(opts);

    // Add to container (newest at top for top positions, at bottom for bottom positions)
    if (opts.position.includes('bottom')) {
      container.insertBefore(element, container.firstChild);
    } else {
      container.appendChild(element);
    }

    // Initialize Lucide icons in the notification element
    requestAnimationFrame(() => {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    });

    // Animate in
    animateIn(element, opts.position);

    // Setup close button
    const closeBtn = element.querySelector('[data-notification-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => close(id));
    }

    // Track notification data
    const notifData = {
      position: opts.position,
      timer: null,
      progressTween: null
    };

    // Auto-close with progress animation
    if (opts.duration > 0) {
      // Start progress animation
      notifData.progressTween = animateProgress(element, opts.duration);

      // Set auto-close timer
      notifData.timer = setTimeout(() => close(id), opts.duration);
    }

    // Store tracking data
    activeNotifications.set(id, notifData);

    return id;
  }

  /**
   * Close all notifications
   */
  function closeAll() {
    activeNotifications.forEach((_, id) => {
      close(id);
    });
  }

  /**
   * Shorthand methods
   */
  function info(title, message, options = {}) {
    return show({ ...options, type: 'info', title, message });
  }

  function success(title, message, options = {}) {
    return show({ ...options, type: 'success', title, message });
  }

  function warning(title, message, options = {}) {
    return show({ ...options, type: 'warning', title, message });
  }

  function error(title, message, options = {}) {
    return show({ ...options, type: 'error', title, message });
  }

  /**
   * Cart notification - shows product image and details
   */
  function cart(title, message, options = {}) {
    return show({ 
      ...options, 
      type: 'cart', 
      title, 
      message,
      showProgress: false
    });
  }

  // Expose API globally (avoid overwriting browser Notification)
  window.ThemeNotification = {
    show,
    close,
    closeAll,
    info,
    success,
    warning,
    error,
    cart
  };

  // Also expose as UntamedNotification for clarity
  window.UntamedNotification = window.ThemeNotification;
})();
